import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { easing, reducedMotion } from './easing';

gsap.registerPlugin(ScrollTrigger);

const modeSelection = new WeakMap<HTMLElement, number>();
const modeHasSwitched = new WeakSet<HTMLElement>();
const mobileModeSelection = new WeakMap<HTMLElement, number>();
const controlAnimations = new WeakMap<HTMLElement, gsap.core.Timeline>();

function replaceAnimation(root: HTMLElement) {
  controlAnimations.get(root)?.kill();
  const timeline = gsap.timeline({ onComplete: () => ScrollTrigger.refresh() });
  controlAnimations.set(root, timeline);
  return timeline;
}

function timing(duration: number, immediate = false) {
  return {
    duration: immediate || reducedMotion() ? 0 : duration,
    ease: easing.cubicOut,
    overwrite: 'auto' as const,
  };
}

/** Source a-43–46: hamburger 300 ms, menu height 800 ms. */
export function animateMenu(root: HTMLElement, open: boolean) {
  const timeline = replaceAnimation(root);
  timeline.to(
    root.querySelectorAll('.menu_line.top'),
    { ...timing(0.3), rotation: open ? 45 : 0, y: open ? 4 : 0 },
    0,
  );
  timeline.to(
    root.querySelectorAll('.menu_line.bottom'),
    { ...timing(0.3), rotation: open ? -45 : 0, y: open ? -4 : 0 },
    0,
  );
  timeline.to(
    root.querySelectorAll('.mobile_menu'),
    { ...timing(0.8), height: open ? '12.5rem' : '0rem' },
    0,
  );
  return timeline;
}

/** Source a-49–54: desktop width / mobile accordion, both 500 ms. */
export function animateModes(root: HTMLElement, activeIndex: number, mobile = false) {
  const selection = mobile ? mobileModeSelection : modeSelection;
  const previous = selection.get(root);
  const immediate = previous === undefined || previous === activeIndex;
  selection.set(root, activeIndex);
  const panels = [
    ...root.querySelectorAll<HTMLElement>(
      mobile ? '.tabs_content.is-mobile' : '.tabs_content:not(.is-mobile)',
    ),
  ];
  const timeline = replaceAnimation(root);
  if (!mobile && !immediate && !modeHasSwitched.has(root)) {
    // IX2 reads the first CSS-sized panel in pixels, then interpolates that
    // number as a percentage. Preserve the observed first-switch curve;
    // subsequent switches in the source start from stored percentage values.
    const initialPanel = panels[previous];
    if (initialPanel)
      gsap.set(initialPanel, { width: `${initialPanel.getBoundingClientRect().width}%` });
    modeHasSwitched.add(root);
  }
  panels.forEach((panel, index) => {
    if (!mobile) {
      timeline.to(
        panel,
        { ...timing(0.5, immediate), width: index === activeIndex ? '100%' : '0%' },
        0,
      );
      return;
    }
    if (index === activeIndex) {
      timeline.set(panel, { display: 'block' }, 0);
      timeline.to(panel, { ...timing(0.5, immediate), height: 'auto' }, 0);
    } else {
      timeline.to(panel, { ...timing(0.5, immediate), height: 0 }, 0);
      timeline.set(panel, { display: 'none' }, immediate || reducedMotion() ? 0 : 0.5);
    }
  });
  return timeline;
}
